import { useCallback, useEffect, useState } from 'react';
import { useUtmTracking } from '../hooks/useUtmTracking';
import { trackEvent, trackCustomEvent } from '../lib/tracking';
import { UrgencyBanner } from '../components/shared/UrgencyBanner';
import { Logo } from '../components/shared/Logo';
import { BookingsCounter, type BookingsPhase } from '../components/shared/BookingsCounter';
import { SocialProofTicker } from '../components/shared/SocialProofTicker';

function getUserDataFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const fullName = params.get('name') ?? '';
  const parts = fullName.trim().split(' ');
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
    email: params.get('email') ?? '',
    phone: params.get('phone') ?? '',
  };
}

const BOOKINGS_MAX = 100;
const SOLD_OUT_FLASH_MS = 1800;
const BOOKINGS_COUNT_KEY = 'hit_bookings_count';
const BOOKINGS_PHASE_KEY = 'hit_bookings_phase';

function loadInitialBookings(): { count: number; phase: BookingsPhase } {
  try {
    const savedCount = sessionStorage.getItem(BOOKINGS_COUNT_KEY);
    const savedPhase = sessionStorage.getItem(BOOKINGS_PHASE_KEY);

    let phase: BookingsPhase = 'climbing';
    if (savedPhase === 'released') phase = 'released';
    else if (savedPhase === 'sold-out') phase = 'released'; // collapse transient flash

    if (savedCount !== null) {
      let count = parseInt(savedCount, 10);
      if (Number.isFinite(count)) {
        // If we collapsed sold-out → released, normalize 100 → 99 (cupo liberado).
        if (savedPhase === 'sold-out' && count === BOOKINGS_MAX) {
          count = BOOKINGS_MAX - 1;
        }
        return { count, phase };
      }
    }
  } catch {
    // sessionStorage unavailable — fall through to fresh start
  }

  const variants = [84, 88, 91];
  return { count: variants[Math.floor(Math.random() * variants.length)], phase: 'climbing' };
}

// Calendario NORMAL (tiers C/D). Debe apuntar a la URL real de booking.
const GHL_CALENDAR_URL = (import.meta.env.VITE_GHL_CALENDAR_URL as string) ?? '';
// Calendario de PRIORIDAD (tiers A/B). Si no está configurado, A/B caen al normal.
const GHL_CALENDAR_URL_PRIORITY = (import.meta.env.VITE_GHL_CALENDAR_URL_PRIORITY as string) ?? '';

// Soporte humano. MISMO número que /agenda y /gracias de hit-vsl.
const SUPPORT_WHATSAPP = '16402213153';

export default function SchedulingPage() {
  const { utmParams } = useUtmTracking();
  const userData = getUserDataFromUrl();

  // Tier de calidad que llega por query desde ApplicationPage (computePerfil).
  // 'D' = NO califica: o dijo que no asistiría, o todavía factura por debajo
  // del umbral (ver INGRESOS_DESCARTE en lib/segmentation.ts).
  // A esa persona NO se le muestra calendario: se le da una salida humana.
  // FAIL-OPEN deliberado: sin tier legible, o con cualquier otro valor, la
  // página se comporta como siempre. Nunca dejar a nadie fuera por un bug.
  const tier = (new URLSearchParams(window.location.search).get('tier') ?? '')
    .trim()
    .toUpperCase();
  const noCalifica = tier === 'D';

  // 3 variantes de arranque (84/88/91) + persistencia sessionStorage.
  const initial = loadInitialBookings();
  const [bookings, setBookings] = useState<number>(initial.count);
  const [phase, setPhase] = useState<BookingsPhase>(initial.phase);
  const [calendarLoaded, setCalendarLoaded] = useState<boolean>(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(BOOKINGS_COUNT_KEY, String(bookings));
    } catch {
      /* ignore */
    }
  }, [bookings]);

  useEffect(() => {
    try {
      sessionStorage.setItem(BOOKINGS_PHASE_KEY, phase);
    } catch {
      /* ignore */
    }
  }, [phase]);

  const bumpBookings = useCallback(() => {
    setBookings(prev => {
      if (prev >= BOOKINGS_MAX) return prev;
      const next = prev + 1;
      if (next >= BOOKINGS_MAX) {
        setPhase('sold-out');
        setTimeout(() => {
          setBookings(BOOKINGS_MAX - 1);
          setPhase('released');
        }, SOLD_OUT_FLASH_MS);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    // Si no califica no hay nada que medir como conversión ni calendario que
    // cargar: mandar su CompleteRegistration le enseña a Meta a traernos más
    // gente igual, que es justo lo que no queremos (misma puerta que aplica
    // mc-capi server-side para el embudo hitvsl).
    if (noCalifica) return;

    // CompleteRegistration = "registro/aplicación completada": la persona pasó la
    // encuesta y LLEGÓ a la página de agenda. NO es InitiateCheckout (acá no hay
    // pago; el checkout es post-llamada). Schedule tampoco va acá: se dispara en la
    // confirmación con la reserva real. Esta página no redirige → el evento de
    // navegador sale confiable y se ve en "Probar eventos".
    trackEvent('CompleteRegistration', { ...utmParams });
    trackCustomEvent('ApplicationApproved', { ...utmParams });

    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [noCalifica]);

  // ─── NO CALIFICA ───────────────────────────────────────────────────────────
  // Calendario vacío + salida humana. Reglas que no hay que romper al iterar:
  // 1. NO se le dice "no calificas" ni "fuiste rechazado". El puntaje mide en
  //    qué punto está su negocio, no cuánto vale ella.
  // 2. NO hay urgencia, contador de cupos ni prueba social. No hay nada que
  //    vender aquí; montar escasez sobre un no es la venta que no hacemos.
  // 3. SIEMPRE hay salida humana: el botón de WhatsApp es el derecho a réplica.
  // 4. NO se dispara ningún evento de conversión (ver el efecto de arriba).
  if (noCalifica) {
    const waMensaje =
      'Hola, acabo de aplicar a HIT y me salió que hoy no es mi momento. ' +
      'Creo que mi situación no quedó bien reflejada en el formulario ¿Me ayudas?';
    const waUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(waMensaje)}`;

    return (
      <div className="min-h-screen bg-hit-black flex flex-col">
        <UrgencyBanner
          icon="✓"
          message="Recibimos tu aplicación. La leímos completa."
          variant="neutral"
        />

        <div className="max-w-xl mx-auto w-full flex flex-col flex-1">
          <div className="pt-6 pb-2 px-4 text-center">
            <Logo />
          </div>

          <div className="px-4 pt-4 pb-2 text-center">
            <h1 className="text-2xl font-extrabold text-hit-white mb-3 leading-snug">
              {userData.firstName ? `${userData.firstName}, hoy` : 'Hoy'} no vamos a
              agendarte una sesión. Y prefiero decírtelo de frente.
            </h1>
            <p className="text-hit-gray-mid text-sm leading-relaxed">
              Por lo que nos contaste,{' '}
              <strong className="text-hit-white">
                tu negocio todavía no está en el punto donde HIT hace su trabajo
              </strong>
              . Y eso no es un «no» a ti: es un «no» a venderte algo que hoy no te va a
              devolver lo que te cuesta.
            </p>
            <p className="text-hit-gray-mid text-sm leading-relaxed mt-3">
              La Certificación HIT es para quien{' '}
              <strong className="text-hit-white">ya tiene un negocio funcionando</strong> y
              necesita convertir eso en eventos que llenen sala. No sirve para arrancar:
              sirve para multiplicar lo que ya está en marcha.
            </p>
          </div>

          <div className="px-4 py-5">
            <div className="bg-hit-dark border border-hit-gray-dark/40 rounded-lg p-5">
              <p className="text-hit-gold text-[11px] font-extrabold tracking-widest uppercase mb-3">
                Qué sí te sirve hoy
              </p>
              <ul className="space-y-3 text-sm text-hit-white">
                <li>
                  <strong>Vuelve al entrenamiento y aplícalo tal cual.</strong>
                  <span className="block text-hit-gray-mid text-[13px] mt-1">
                    No necesitas pagar nada para empezar por ahí.
                  </span>
                </li>
                <li>
                  <strong>Construye lo que hoy falta: que alguien ya te compre.</strong>
                  <span className="block text-hit-gray-mid text-[13px] mt-1">
                    Una oferta, un canal, ventas que se repitan. Ese es el único
                    requisito real. No es un filtro de dinero, es de etapa.
                  </span>
                </li>
                <li>
                  <strong>Quédate en la lista.</strong>
                  <span className="block text-hit-gray-mid text-[13px] mt-1">
                    Cuando tu negocio llegue a ese punto, vuelve a aplicar — y esta vez
                    te sentamos con un mentor.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="px-4 pb-8">
            <div className="bg-hit-dark/60 border border-hit-gray-dark/30 rounded-lg p-5 text-center">
              <p className="text-hit-gray-mid text-[13px] leading-relaxed">
                <strong className="text-hit-white">¿Crees que nos equivocamos?</strong>{' '}
                Puede pasar. Decidimos con trece respuestas y trece respuestas no son una
                persona. Si tu situación real no entró en ese formulario, escríbenos y lo
                miramos a mano.
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg px-6 py-3 transition-colors text-sm"
              >
                📱 Escribir por WhatsApp
              </a>
            </div>

            <p className="text-hit-gray-mid text-[12px] text-center mt-6 leading-relaxed">
              Gracias por el tiempo que le dedicaste a la aplicación.
              <br />
              Decirte que no hoy es la forma honesta de decirte que sí más adelante.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const calendarParams = new URLSearchParams({
    first_name: userData.firstName,
    last_name: userData.lastName,
    email: userData.email,
    phone: userData.phone,
    utm_source: utmParams.utm_source ?? '',
    utm_medium: utmParams.utm_medium ?? '',
    utm_campaign: utmParams.utm_campaign ?? '',
    utm_content: utmParams.utm_content ?? '',
    utm_term: utmParams.utm_term ?? '',
  });

  // Enrutamiento por calidad de lead: A/B → calendario de prioridad, C → normal.
  // (D ya salió arriba por el early-return: no ve calendario.) El tier llega por
  // query desde ApplicationPage. Fail-safe: sin tier o sin URL de prioridad, normal.
  const isPriorityTier = tier === 'A' || tier === 'B';
  const baseCalendarUrl =
    isPriorityTier && GHL_CALENDAR_URL_PRIORITY ? GHL_CALENDAR_URL_PRIORITY : GHL_CALENDAR_URL;

  const calendarUrl = baseCalendarUrl
    ? `${baseCalendarUrl}?${calendarParams.toString()}`
    : '';

  const supportWhatsappNumber = SUPPORT_WHATSAPP;
  const supportWhatsappMessage =
    'Hola, Quiero Agendar una Sesión Estratégica para HIT ¿Me ayudas?';
  const supportWhatsappUrl = `https://wa.me/${supportWhatsappNumber}?text=${encodeURIComponent(
    supportWhatsappMessage,
  )}`;

  return (
    <div className="min-h-screen bg-hit-black flex flex-col">
      <UrgencyBanner
        icon="✅"
        message="¡Aplicación aprobada! Agenda tu llamada antes que tome tu cupo otra persona."
        variant="success"
      />

      <div className="max-w-xl mx-auto w-full flex flex-col flex-1">
        <div className="pt-6 pb-2 px-4 text-center">
          <Logo />
        </div>

        <div className="px-4 pt-4 pb-6 text-center">
          <h1 className="text-2xl font-extrabold text-hit-white mb-2">
            🎯 ¡Felicidades
            {userData.firstName ? `, ${userData.firstName}` : ''}!
          </h1>
          <p className="text-hit-gray-mid text-sm">
            Tu aplicación fue recibida y aprobada.
          </p>
          <p className="text-hit-white font-semibold mt-2 text-sm">
            👇 Agenda tu Sesión Estratégica 1-a-1
            <br />
            <span className="text-hit-gold">con un Mentor HIT (valorada en $497 USD)</span>
          </p>
        </div>

        <div className="px-4 mb-6">
          <BookingsCounter count={bookings} max={BOOKINGS_MAX} phase={phase} />
        </div>

        <div className="px-4 mb-2">
          <p className="text-[11px] text-hit-gray-mid text-center leading-snug">
            ⏰ Los horarios están en <strong className="text-hit-white">formato 24 horas</strong>.
            <br />
            Ej: 03:00 = 3:00 a. m. · 15:00 = 3:00 p. m.
          </p>
        </div>

        <div className="px-4 mb-6">
          {calendarUrl ? (
            <div className="relative" style={{ minHeight: '700px' }}>
              {!calendarLoaded && (
                <div
                  className="absolute inset-0 bg-hit-dark border border-hit-gray-dark/40 rounded-lg flex flex-col items-center justify-center px-6"
                  aria-live="polite"
                >
                  <div className="w-10 h-10 rounded-full border-2 border-hit-gold/30 border-t-hit-gold animate-spin mb-4" />
                  <p className="text-hit-white text-sm font-semibold text-center">
                    Cargando horarios en tu zona horaria…
                  </p>
                  <p className="text-hit-gray-mid text-xs text-center mt-2 leading-relaxed">
                    Esto tarda unos segundos. No cierres esta página.
                  </p>
                  <div className="mt-6 w-full max-w-xs space-y-2">
                    <div className="h-3 bg-hit-gray-dark/40 rounded animate-pulse" />
                    <div className="h-3 bg-hit-gray-dark/30 rounded animate-pulse" style={{ animationDelay: '120ms' }} />
                    <div className="h-3 bg-hit-gray-dark/20 rounded animate-pulse" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              )}
              <iframe
                src={calendarUrl}
                style={{
                  width: '100%',
                  border: 'none',
                  minHeight: '700px',
                  opacity: calendarLoaded ? 1 : 0,
                  transition: 'opacity 250ms ease-out',
                }}
                scrolling="no"
                id="ghl_calendar"
                title="Agenda tu sesión HIT"
                loading="eager"
                onLoad={() => setCalendarLoaded(true)}
              />
            </div>
          ) : (
            <div className="bg-hit-dark border border-hit-gray-dark rounded-lg p-8 text-center">
              <p className="text-hit-gray-mid text-sm">
                El calendario de agendamiento estará disponible en Fase 3.
                <br />
                <span className="text-xs text-hit-gray-dark mt-2 block">
                  (VITE_GHL_CALENDAR_URL pendiente de configuración)
                </span>
              </p>
            </div>
          )}
        </div>

        <div className="px-4 mb-4 bg-hit-dark border border-hit-gray-dark/30 rounded-lg mx-4 py-4 text-center">
          <p className="text-hit-gray-mid text-sm">
            ⚠️ Solo agendamos 1 sesión por día.
            <br />
            Si no encuentras horario disponible, contáctanos.
          </p>
        </div>

        <div className="px-4 pb-8 text-center">
          <a
            href={supportWhatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg px-6 py-3 transition-colors text-sm"
          >
            📱 Hablar con Soporte por WhatsApp
          </a>
        </div>
      </div>

      <SocialProofTicker
        enabled={calendarLoaded && phase === 'climbing'}
        minIntervalMs={17000}
        maxIntervalMs={17000}
        onAppear={bumpBookings}
      />
    </div>
  );
}
